/**
 * cart-promo-bxgy.js
 *
 * BXGY 도메인 헬퍼 (Phase 4b+4c, 2026-05-27).
 *
 * 책임: BXGY 모달 후보 매칭·계산·마킹 (DOM read + ctx 매개변수 only).
 *   - cart 라인 ↔ candidate 매칭
 *   - catalog candidate visible 갯수
 *   - cart-item--gift 마킹 (DOM mutation, #main-cart-items 한정)
 *
 * 상태 모델: ctx (this.state.bxgyContext) 는 hub 가 보유, wrapper 가 매개변수로 전달.
 * → 모듈 안 함수는 순수 (state 미참조). 호출부 무수정.
 *
 * 의존:
 *   - window.CartCouponHubUtils (bxgySelectionId, bxgyLineQuantity, shopifyNumericId)
 *
 * 로드 순서:
 *   1. cart-coupon-utils.js
 *   2. cart-promo-effects.js
 *   3. cart-promo-storefront.js
 *   4. cart-promo-bxgy.js  ← 본 파일
 *   5. cart-coupon-hub.js
 *
 * 회귀 박제:
 *   - applyGiftMarking remove 스코프 #main-cart-items 한정 (2026-05-26) — 드로어 SSR 마킹 보존
 *   - anchor flag SSOT (2026-05-24) — DOM 순서 추론 금지
 *   - bxgyCartCandidateCells anchor-only (2026-05-27) — GY 항상 catalog 신규 add
 *   - isCatalogItemAlreadyRepresented (variant 매칭) — 이미 다른 promo 적용 라인 / 현재 anchor 제외
 */
(function (global) {
  'use strict';

  const Utils = global.CartCouponHubUtils;
  if (!Utils) {
    console.warn('[cart-promo-bxgy] CartCouponHubUtils 미로드 — 로드 순서 확인');
  }

  const Bxgy = {

    // 🔴 BXGY 부모/자식 마킹 — anchor flag (data-applied-promotion-anchor='true') 우선.
    // 사용자 정책: "처음에 카트에서 프로모션 적용한 상품(=anchor) 이 부모, 그 외 같은 _promotion_id 라인 모두 ㄴ 자식".
    // anchor 가 없는 promo 묶음 (legacy / SSR 누락) 은 cart DOM 첫 라인 fallback.
    // 🔴 remove 스코프 = #main-cart-items 한정 (2026-05-26). 드로어는 SSR (cart-drawer.liquid) 가
    //    is_bxgy_gift 로 cart-item--gift 마킹. 전체 도큐먼트 remove 시 드로어 SSR 마킹까지 strip 되어 ㄴ connector 깨짐.
    // 🔴 (2026-05-28) group_id 기준 묶음. legacy 호환으로 promo_id fallback.
    //   다른 anchor 의 같은 promo 묶음이 group_id 다르면 별도 그룹 처리 (정합).
    applyGiftMarking() {
      // 기존 마킹 모두 제거 (idempotent)
      document.querySelectorAll('#main-cart-items .cart-item--gift').forEach((el) => el.classList.remove('cart-item--gift'));

      const cells = Array.from(document.querySelectorAll('#main-cart-items [data-cart-promo-cell]'));
      const byGroup = new Map();
      cells.forEach((cell) => {
        if (cell.dataset.appliedPromotionType !== 'buy_x_get_y') return;
        const groupId = (cell.dataset.appliedPromotionGroupId || '').trim();
        const promoId = (cell.dataset.appliedPromotionId || '').trim();
        const key = groupId || promoId;
        if (!key) return;
        if (!byGroup.has(key)) byGroup.set(key, []);
        byGroup.get(key).push(cell);
      });

      byGroup.forEach((groupCells) => {
        const anchorCell = groupCells.find((c) => c.dataset.appliedPromotionAnchor === 'true');
        const parentCell = anchorCell || groupCells[0]; // fallback: DOM 첫 라인
        groupCells.forEach((cell) => {
          if (cell === parentCell) return;
          cell.closest('.cart-item')?.classList.add('cart-item--gift');
        });
      });
    },

    // 🔴 [정책] anchor cell 만 반환 (2026-05-27). GY 항상 catalog 신규 add 정책.
    //   이전엔 anchor + 같은 promo 적용 cart 라인 + BXGY 자격 가진 cart 라인 모두 반환 → cleanup 분기 복잡.
    //   anchor 만 cart-source 로 두고 buy role 진입점 역할. 다른 buy/GY 후보는 모두 catalog (selectableProducts).
    //   회귀 case: wiki/docs/bxgy-reentry-cleanup-and-cart-remove-button-capture-2026-05-26.md
    cartCandidateCells(ctx) {
      if (!ctx?.anchorSelectionId) return [];
      const anchorCell = Array.from(document.querySelectorAll('[data-cart-promo-cell]')).find(
        (cell) => Utils.bxgySelectionId(cell) === ctx.anchorSelectionId
      );
      return anchorCell ? [anchorCell] : [];
    },

    // selectionId 로 candidate 매칭. cart cell (anchor) 우선, catalog 후순위.
    candidateById(id, ctx) {
      if (!ctx) return null;
      const cartCell = Bxgy.cartCandidateCells(ctx).find((cell) => Utils.bxgySelectionId(cell) === id);
      if (cartCell) return { id, source: 'cart', cell: cartCell };
      const normalizedId = String(id || '');
      return (ctx.selectableProducts || []).find((item) => {
        return item.id === normalizedId ||
          item.variantId === normalizedId ||
          item.variantGid === normalizedId ||
          normalizedId.includes(`:${item.variantGid}:`) ||
          normalizedId.includes(`/${item.variantId}:`);
      }) || null;
    },

    // cart 라인과 variant 매칭으로 화면에 안 보이는 catalog 갯수 제외 후 visible count.
    // autoFill 트리거 + Load more hidden 판단 등 여러 곳에서 사용.
    catalogVisibleCount(ctx) {
      return (ctx?.selectableProducts || []).filter(
        (item) => !Bxgy.isCatalogItemAlreadyRepresented(item, ctx)
      ).length;
    },

    // 🔴 (2026-05-28) anchor 분기 제거 — anchor variant 가 GY 컬렉션에 있을 때 catalog 에 표시 + 선택 가능 (A11).
    //   같은 variant 라도 properties 차이 (role buy vs get, group_id) 로 Shopify auto-merge 차단 → 별도 라인.
    //   다른 promo 적용된 같은 variant 만 제외 (다른 promo 충돌 방지).
    isCatalogItemAlreadyRepresented(item, ctx) {
      const promoId = ctx?.promotion?.id || '';
      const itemVariantId = Utils.shopifyNumericId(item.variantId || item.variantGid);
      return Array.from(document.querySelectorAll('[data-cart-promo-cell]')).some((cell) => {
        const sameVariant =
          itemVariantId &&
          (cell.dataset.variantId === itemVariantId ||
            Utils.shopifyNumericId(cell.dataset.variantGid) === itemVariantId);
        if (!sameVariant) return false;
        if (cell.dataset.appliedPromotionId && cell.dataset.appliedPromotionId !== promoId) return true;
        return false;
      });
    },

    // 🔴 anchor 가 cart 라인 (qty>=2 가능) 인 경우 실제 line.quantity 합산 (2026-05-27).
    //   회귀: 이전 `return sum + 1` 하드코딩 → anchor qty=3 인 BXGY 3+1 promo 에서
    //   selectedQuantity=1 로 잡혀 한도(4) 까지 buy 2개 + get 1개 추가 선택 가능.
    //   fix: anchor lineQty 합산 → 정확한 selectedQuantity.
    selectedQuantity(ctx, lookupById = null) {
      if (!ctx) return 0;
      return ctx.selectedIds.reduce((sum, id) => {
        if (id === ctx.anchorSelectionId) {
          const anchorCell = ctx.anchorCell || document.querySelector(
            `[data-cart-promo-cell][data-line-key="${CSS.escape(ctx.lineKey || '')}"]`
          );
          return sum + (anchorCell ? Utils.bxgyLineQuantity(anchorCell) : 1);
        }
        if (Object.prototype.hasOwnProperty.call(ctx.addQuantities || {}, id)) {
          const quantity = Number(ctx.addQuantities[id]);
          return sum + (Number.isFinite(quantity) && quantity > 0 ? quantity : 1);
        }
        const candidate = lookupById?.get(id) || Bxgy.candidateById(id, ctx);
        if (candidate?.source === 'cart') return sum + Utils.bxgyLineQuantity(candidate.cell);
        return sum + Math.max(1, parseInt(candidate?.quantity || '1', 10) || 1);
      }, 0);
    },
  };

  global.CartPromoBxgy = Bxgy;
})(typeof window !== 'undefined' ? window : globalThis);
